/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  getNonNullResolvedInstructionInput,
  type ResolvedInstructionAccount,
} from "@solana/program-client-core";
import { findEventAuthorityPda, findRewardVaultPda } from "../pdas/index.js";
import { CP_AMM_PROGRAM_ADDRESS } from "../programs/index.js";

export const FUND_REWARD_DISCRIMINATOR: ReadonlyUint8Array = new Uint8Array([
  188, 50, 249, 165, 93, 151, 38, 63,
]);

export function getFundRewardDiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(FUND_REWARD_DISCRIMINATOR);
}

export type FundRewardInstruction<
  TProgram extends string = typeof CP_AMM_PROGRAM_ADDRESS,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountRewardVault extends string | AccountMeta<string> = string,
  TAccountRewardMint extends string | AccountMeta<string> = string,
  TAccountFunderTokenAccount extends string | AccountMeta<string> = string,
  TAccountFunder extends string | AccountMeta<string> = string,
  TAccountTokenProgram extends string | AccountMeta<string> =
    "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA",
  TAccountEventAuthority extends string | AccountMeta<string> = string,
  TAccountProgram extends string | AccountMeta<string> =
    "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPool extends string
        ? WritableAccount<TAccountPool>
        : TAccountPool,
      TAccountRewardVault extends string
        ? WritableAccount<TAccountRewardVault>
        : TAccountRewardVault,
      TAccountRewardMint extends string
        ? ReadonlyAccount<TAccountRewardMint>
        : TAccountRewardMint,
      TAccountFunderTokenAccount extends string
        ? WritableAccount<TAccountFunderTokenAccount>
        : TAccountFunderTokenAccount,
      TAccountFunder extends string
        ? ReadonlySignerAccount<TAccountFunder> &
            AccountSignerMeta<TAccountFunder>
        : TAccountFunder,
      TAccountTokenProgram extends string
        ? ReadonlyAccount<TAccountTokenProgram>
        : TAccountTokenProgram,
      TAccountEventAuthority extends string
        ? ReadonlyAccount<TAccountEventAuthority>
        : TAccountEventAuthority,
      TAccountProgram extends string
        ? ReadonlyAccount<TAccountProgram>
        : TAccountProgram,
      ...TRemainingAccounts,
    ]
  >;

export type FundRewardInstructionData = {
  discriminator: ReadonlyUint8Array;
  rewardIndex: number;
  amount: bigint;
  carryForward: boolean;
};

export type FundRewardInstructionDataArgs = {
  rewardIndex: number;
  amount: number | bigint;
  carryForward: boolean;
};

export function getFundRewardInstructionDataEncoder(): FixedSizeEncoder<FundRewardInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["rewardIndex", getU8Encoder()],
      ["amount", getU64Encoder()],
      ["carryForward", getBooleanEncoder()],
    ]),
    (value) => ({ ...value, discriminator: FUND_REWARD_DISCRIMINATOR }),
  );
}

export function getFundRewardInstructionDataDecoder(): FixedSizeDecoder<FundRewardInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["rewardIndex", getU8Decoder()],
    ["amount", getU64Decoder()],
    ["carryForward", getBooleanDecoder()],
  ]);
}

export function getFundRewardInstructionDataCodec(): FixedSizeCodec<
  FundRewardInstructionDataArgs,
  FundRewardInstructionData
> {
  return combineCodec(
    getFundRewardInstructionDataEncoder(),
    getFundRewardInstructionDataDecoder(),
  );
}

export type FundRewardAsyncInput<
  TAccountPool extends string = string,
  TAccountRewardVault extends string = string,
  TAccountRewardMint extends string = string,
  TAccountFunderTokenAccount extends string = string,
  TAccountFunder extends string = string,
  TAccountTokenProgram extends string = string,
  TAccountEventAuthority extends string = string,
  TAccountProgram extends string = string,
> = {
  pool: Address<TAccountPool>;
  rewardVault?: Address<TAccountRewardVault>;
  rewardMint: Address<TAccountRewardMint>;
  funderTokenAccount: Address<TAccountFunderTokenAccount>;
  funder: TransactionSigner<TAccountFunder>;
  tokenProgram?: Address<TAccountTokenProgram>;
  eventAuthority?: Address<TAccountEventAuthority>;
  program?: Address<TAccountProgram>;
  rewardIndex: FundRewardInstructionDataArgs["rewardIndex"];
  amount: FundRewardInstructionDataArgs["amount"];
  carryForward: FundRewardInstructionDataArgs["carryForward"];
};

export async function getFundRewardInstructionAsync<
  TAccountPool extends string,
  TAccountRewardVault extends string,
  TAccountRewardMint extends string,
  TAccountFunderTokenAccount extends string,
  TAccountFunder extends string,
  TAccountTokenProgram extends string,
  TAccountEventAuthority extends string,
  TAccountProgram extends string,
  TProgramAddress extends Address = typeof CP_AMM_PROGRAM_ADDRESS,
>(
  input: FundRewardAsyncInput<
    TAccountPool,
    TAccountRewardVault,
    TAccountRewardMint,
    TAccountFunderTokenAccount,
    TAccountFunder,
    TAccountTokenProgram,
    TAccountEventAuthority,
    TAccountProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  FundRewardInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountRewardVault,
    TAccountRewardMint,
    TAccountFunderTokenAccount,
    TAccountFunder,
    TAccountTokenProgram,
    TAccountEventAuthority,
    TAccountProgram
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? CP_AMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: true },
    rewardVault: { value: input.rewardVault ?? null, isWritable: true },
    rewardMint: { value: input.rewardMint ?? null, isWritable: false },
    funderTokenAccount: {
      value: input.funderTokenAccount ?? null,
      isWritable: true,
    },
    funder: { value: input.funder ?? null, isWritable: false },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
    eventAuthority: { value: input.eventAuthority ?? null, isWritable: false },
    program: { value: input.program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.rewardVault.value) {
    accounts.rewardVault.value = await findRewardVaultPda({
      pool: getAddressFromResolvedInstructionAccount(
        "pool",
        accounts.pool.value,
      ),
      rewardIndex: getNonNullResolvedInstructionInput(
        "rewardIndex",
        args.rewardIndex,
      ),
    });
  }
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }
  if (!accounts.eventAuthority.value) {
    accounts.eventAuthority.value = await findEventAuthorityPda();
  }
  if (!accounts.program.value) {
    accounts.program.value =
      "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG" as Address<"cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("pool", accounts.pool),
      getAccountMeta("rewardVault", accounts.rewardVault),
      getAccountMeta("rewardMint", accounts.rewardMint),
      getAccountMeta("funderTokenAccount", accounts.funderTokenAccount),
      getAccountMeta("funder", accounts.funder),
      getAccountMeta("tokenProgram", accounts.tokenProgram),
      getAccountMeta("eventAuthority", accounts.eventAuthority),
      getAccountMeta("program", accounts.program),
    ],
    data: getFundRewardInstructionDataEncoder().encode(
      args as FundRewardInstructionDataArgs,
    ),
    programAddress,
  } as FundRewardInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountRewardVault,
    TAccountRewardMint,
    TAccountFunderTokenAccount,
    TAccountFunder,
    TAccountTokenProgram,
    TAccountEventAuthority,
    TAccountProgram
  >);
}

export type FundRewardInput<
  TAccountPool extends string = string,
  TAccountRewardVault extends string = string,
  TAccountRewardMint extends string = string,
  TAccountFunderTokenAccount extends string = string,
  TAccountFunder extends string = string,
  TAccountTokenProgram extends string = string,
  TAccountEventAuthority extends string = string,
  TAccountProgram extends string = string,
> = {
  pool: Address<TAccountPool>;
  rewardVault: Address<TAccountRewardVault>;
  rewardMint: Address<TAccountRewardMint>;
  funderTokenAccount: Address<TAccountFunderTokenAccount>;
  funder: TransactionSigner<TAccountFunder>;
  tokenProgram?: Address<TAccountTokenProgram>;
  eventAuthority: Address<TAccountEventAuthority>;
  program?: Address<TAccountProgram>;
  rewardIndex: FundRewardInstructionDataArgs["rewardIndex"];
  amount: FundRewardInstructionDataArgs["amount"];
  carryForward: FundRewardInstructionDataArgs["carryForward"];
};

export function getFundRewardInstruction<
  TAccountPool extends string,
  TAccountRewardVault extends string,
  TAccountRewardMint extends string,
  TAccountFunderTokenAccount extends string,
  TAccountFunder extends string,
  TAccountTokenProgram extends string,
  TAccountEventAuthority extends string,
  TAccountProgram extends string,
  TProgramAddress extends Address = typeof CP_AMM_PROGRAM_ADDRESS,
>(
  input: FundRewardInput<
    TAccountPool,
    TAccountRewardVault,
    TAccountRewardMint,
    TAccountFunderTokenAccount,
    TAccountFunder,
    TAccountTokenProgram,
    TAccountEventAuthority,
    TAccountProgram
  >,
  config?: { programAddress?: TProgramAddress },
): FundRewardInstruction<
  TProgramAddress,
  TAccountPool,
  TAccountRewardVault,
  TAccountRewardMint,
  TAccountFunderTokenAccount,
  TAccountFunder,
  TAccountTokenProgram,
  TAccountEventAuthority,
  TAccountProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? CP_AMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: true },
    rewardVault: { value: input.rewardVault ?? null, isWritable: true },
    rewardMint: { value: input.rewardMint ?? null, isWritable: false },
    funderTokenAccount: {
      value: input.funderTokenAccount ?? null,
      isWritable: true,
    },
    funder: { value: input.funder ?? null, isWritable: false },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
    eventAuthority: { value: input.eventAuthority ?? null, isWritable: false },
    program: { value: input.program ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }
  if (!accounts.program.value) {
    accounts.program.value =
      "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG" as Address<"cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("pool", accounts.pool),
      getAccountMeta("rewardVault", accounts.rewardVault),
      getAccountMeta("rewardMint", accounts.rewardMint),
      getAccountMeta("funderTokenAccount", accounts.funderTokenAccount),
      getAccountMeta("funder", accounts.funder),
      getAccountMeta("tokenProgram", accounts.tokenProgram),
      getAccountMeta("eventAuthority", accounts.eventAuthority),
      getAccountMeta("program", accounts.program),
    ],
    data: getFundRewardInstructionDataEncoder().encode(
      args as FundRewardInstructionDataArgs,
    ),
    programAddress,
  } as FundRewardInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountRewardVault,
    TAccountRewardMint,
    TAccountFunderTokenAccount,
    TAccountFunder,
    TAccountTokenProgram,
    TAccountEventAuthority,
    TAccountProgram
  >);
}

export type ParsedFundRewardInstruction<
  TProgram extends string = typeof CP_AMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    pool: TAccountMetas[0];
    rewardVault: TAccountMetas[1];
    rewardMint: TAccountMetas[2];
    funderTokenAccount: TAccountMetas[3];
    funder: TAccountMetas[4];
    tokenProgram: TAccountMetas[5];
    eventAuthority: TAccountMetas[6];
    program: TAccountMetas[7];
  };
  data: FundRewardInstructionData;
};

export function parseFundRewardInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedFundRewardInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 8) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 8,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      pool: getNextAccount(),
      rewardVault: getNextAccount(),
      rewardMint: getNextAccount(),
      funderTokenAccount: getNextAccount(),
      funder: getNextAccount(),
      tokenProgram: getNextAccount(),
      eventAuthority: getNextAccount(),
      program: getNextAccount(),
    },
    data: getFundRewardInstructionDataDecoder().decode(instruction.data),
  };
}
